import { saveRequestCorporateAccountRequest } from "../../../../app/api/requestCorporateAccount";
import { BaseAction } from "../../../../core/actions/BaseAction";
import { isEmpty } from "../../../../Lib/Utils/validationUtil";
import {
    COMPANY_NAME_MAX_LENGTH,
    isValidCompanyName,
    NAME_MIN_LENGTH,
} from "../../../../Lib/Utils/serviceRequestValidation";
import { updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse } from "../../../../Orchestrator/Types/Index";
import { ISaveRequestCorporateAccountParams } from "../../../../Orchestrator/Types/Interface/ActionParams/RequestCorporate";
import { IRequestCorporateAccount, IRequestCorporateAccountRequest, IRequestCorporateAccountResponse } from "../../../../Orchestrator/Types/Interface/IRequestcorporate";


/**
 * `+<country code><number>`. The picker stores the code bare ("971") and the
 * number is typed free-form, so both are reduced to digits before joining —
 * the API documents its phone fields in E.164.
 */
const toE164 = (dialCode: string, phoneNumber: string) =>
    `+${(dialCode || "").replace(/\D/g, "")}${(phoneNumber || "").replace(/\D/g, "")}`;

/**
 * Maps the form to POST /corporates/apply. The company has no separate contact
 * details on this form, so `data.mobile` / `data.corporateEmail` repeat the
 * admin's — which is how the endpoint's own example fills them.
 */
export const buildCorporateApplyPayload = (
    details: IRequestCorporateAccount,
): IRequestCorporateAccountRequest => ({
    name: details.companyName,
    admin: {
        firstName: details.accountAdminFirstName,
        lastName: details.accountAdminLastName,
        email: details.email,
        phoneNumber: toE164(details.mobileCode, details.phoneNumber),
    },
    data: {
        commercialLicenseNumber: details.commercialLicense,
        // Labels, not the dropdowns' slugs — these land in free-text fields a
        // person reads ("Technology & Innovation", not
        // "technology_and_innovation").
        activitySector: details.activitySectorLabel || details.activitySector,
        mobile: toE164(details.mobileCode, details.phoneNumber),
        corporateEmail: details.email,
        // The form collects a city but no street, and `address` rejects one
        // without the other — so the city goes in both for now. Deliberate:
        // dropping `address` is legal (nothing in `data` is required) but
        // would discard a field the form makes mandatory. Backend intends to
        // expose a city of its own later; collapse this to that field when it
        // lands rather than duplicating forever.
        address: {
            street: details.cityLabel || details.cityUAE,
            city: details.cityLabel || details.cityUAE,
        },
    },
});

class SaveRequestCorporateAccount extends BaseAction<string, ISaveRequestCorporateAccountParams> {
    /**
     * @function validateForm only use once action call from test file.
    */
    validateForm = () => {
        let result = {
            isValid: true,
            message: ""
        }
        const { oldDetails } = this.params!
        const fields = ["companyName", "activitySector", "commercialLicense", "cityUAE", "accountAdminFirstName", "accountAdminLastName", "email", "phoneNumber"] as const;
        type FieldKey = typeof fields[number];
        const validationMessage: Record<FieldKey, string> = {
            companyName: "Company Name is required",
            activitySector: "Activity Sector is required",
            commercialLicense: "License Number is required",
            cityUAE: "City is required",
            accountAdminFirstName: "First Name is required",
            accountAdminLastName: "Last Name is required",
            email: "Email is required",
            phoneNumber: "Phone Number is required",
        }
        for (let i = 0; i < fields.length; i++) {
            const key = fields[i];
            if (isEmpty(oldDetails[key])) {
                result = {
                    isValid: false,
                    message: validationMessage[key]
                }
                break;
            } else {
                if (key === "companyName" && !isValidCompanyName(oldDetails[key])) {
                    // Same rule the form applies, rather than a second copy of
                    // it that can drift.
                    result = {
                        isValid: false,
                        message: `Company Name must be ${NAME_MIN_LENGTH}-${COMPANY_NAME_MAX_LENGTH} characters and include at least one letter`
                    }
                    break;
                }
            }
        }
        return result
    }
    override async run(): Promise<string> {
        let actionResponse = "fail";
        try {
            const { oldDetails, isFrom = "" } = this.params!
            if (oldDetails) {
                let callAPI = true
                const payload: IRequestCorporateAccountRequest =
                    buildCorporateApplyPayload(oldDetails)
                if (isFrom === "testFile") {
                    const formValidate = this.validateForm()
                    callAPI = formValidate?.isValid || false
                    if (!callAPI) {
                        actionResponse = formValidate?.message || ""
                    }
                }
                if (callAPI) {
                    const response: IApiResponse<IRequestCorporateAccountResponse> = await saveRequestCorporateAccountRequest(payload);
                    if (response.code === 200 && response.answer) {
                        actionResponse = "success"
                        updateRecoilState("corporateFormSubmittedAtom", true)
                    } else {
                        actionResponse = "Something went wrong"
                    }
                }
            }

        } catch (error: any) {
            actionResponse = error?.message || ""
            console.error("Error fetching Cities :", error);
        }
        finally {
            return actionResponse
        }
    }
}

export const saveRequestCorporateAccount = new SaveRequestCorporateAccount()


